import React, { useState, useEffect, useCallback, useRef } from "react";
import { InstantSearch, Configure } from "react-instantsearch-dom";
import { SiteContextProvider } from "@arcteryx/components-contexts";
import { withRouter } from "react-router-dom";
import { adaptSort } from "@arcteryx/data-models";
import { useSSR } from "@arcteryx/components-hooks";
import { createURL, searchStateToUrl, urlToSearchState, buildAlgoliaIndex, buildTagsArray } from "../helpers";
import { IndexContextProvider } from "../context/IndexContext";
import aa from "search-insights";
import algoliasearch from "algoliasearch/lite";

const SearchProvider = ({
  children,
  location,
  history,
  searchDebounceTime,
  stateFromHeader: { market: MARKET, language, country, environment },
  analyticsEvents,
  algoliaAppId,
  algoliaApiKey,
}) => {
  const market = MARKET.toLowerCase();
  const indexName = buildAlgoliaIndex({ environment, market, language, country });
  const sort = adaptSort(indexName);
  const [searchState, setSearchState] = useState(urlToSearchState(location, sort.items));
  const [debouncedSetState, setDebouncedSetState] = useState(null);
  const [analyticsTags, setAnalyticsTags] = useState(null);
  const { isBrowser } = useSSR();
  const algoliaClient = algoliasearch(algoliaAppId, algoliaApiKey);
  const [searchClient, setSearchClient] = useState(null);
  const [suggestedSearch, setSuggestedSearch] = useState(null);
  const [suggestedSearchContext, setSuggestedSearchContext] = useState(null);
  const contextRef = useRef(suggestedSearchContext);

  const isRequestWithFacetFilters = (requests) => {
    return requests.some(({ params }) => params.facetFilters && params.facetFilters.length > 0);
  };

  const isRequestSortBy = (requests) => {
    return requests.some(
      ({ indexName: requestedIndexName }) => requestedIndexName.endsWith("_asc") || requestedIndexName.endsWith("_desc")
    );
  };

  // Init of Algolia Search
  useEffect(() => {
    const searchClientObject = {
      search(requests) {
        if (requests.every(({ params }) => !params.query)) {
          return Promise.resolve({
            results: requests.map(() => ({
              hits: [],
              nbHits: 0,
              nbPages: 0,
              page: 0,
              processingTimeMS: 0,
              hitsPerPage: 0,
              exhaustiveNbHits: false,
              query: "",
              params: "",
            })),
          });
        }

        if (isRequestWithFacetFilters(requests) || isRequestSortBy(requests)) {
          return algoliaClient.search(requests);
        }

        const { results } = contextRef.current;
        return Promise.resolve({ results });
      },
    };
    setSearchClient(searchClientObject);
  }, []);

  // Algolia Analytics
  useEffect(() => {
    const canTrackUser = (window.OnetrustActiveGroups && window.OnetrustActiveGroups.includes(",C0002,")) || true;

    setAnalyticsTags(
      isBrowser && buildTagsArray(window.navigator, canTrackUser ? document.cookie : null, window.location.pathname)
    );

    aa("init", {
      appId: algoliaAppId,
      apiKey: algoliaApiKey,
      useCookie: canTrackUser,
    });
  }, []);

  useEffect(() => {
    contextRef.current = suggestedSearchContext; // Update ref on every change
  }, [suggestedSearchContext]);

  const onSearchStateChange = (updatedSearchState) => {
    if (updatedSearchState.query === undefined) {
      return;
    }

    const { searchTermEntered, searchTermSuggested, staticTerm, termCount, terms } = suggestedSearchContext;
    clearTimeout(debouncedSetState);
    setDebouncedSetState(
      setTimeout(() => {
        history.push(searchStateToUrl(location, updatedSearchState, sort.items));
      }, searchDebounceTime)
    );
    setSearchState(updatedSearchState);

    if (staticTerm) {
      analyticsEvents.handleSearchEvent({ searchTermEntered: staticTerm, staticTerm });
    } else if (searchTermEntered && searchTermSuggested) {
      analyticsEvents.handleSearchEvent({ searchTermEntered, searchTermSuggested, termCount, terms });
    } else {
      analyticsEvents.handleSearchEvent({ searchTermEntered: updatedSearchState.query });
    }
  };

  const handleSetSuggestedSearch = useCallback((search) => setSuggestedSearch(search), []);
  const handleSetSuggestedSearchContext = useCallback((context) => setSuggestedSearchContext(context), []);

  return (
    <SiteContextProvider market={market} country={country} language={language}>
      <IndexContextProvider
        index={indexName}
        analyticsEvents={analyticsEvents}
        algoliaClient={algoliaClient}
        environment={environment}
        suggestedSearch={suggestedSearch}
        setSuggestedSearch={handleSetSuggestedSearch}
        suggestedSearchContext={suggestedSearchContext}
        setSuggestedSearchContext={handleSetSuggestedSearchContext}
      >
        {searchClient && (
          <InstantSearch
            indexName={indexName}
            searchClient={searchClient}
            searchState={searchState}
            onSearchStateChange={onSearchStateChange}
            createURL={createURL}
          >
            <Configure clickAnalytics analyticsTags={analyticsTags} hitsPerPage={999} />
            {children}
          </InstantSearch>
        )}
      </IndexContextProvider>
    </SiteContextProvider>
  );
};
export default withRouter(SearchProvider);
